import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PhotoProvider, PhotoView } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "./ShopProducts.css";
import LoadingAnimation from "../components/LoadingAnimation";
import axiosInstance from "../api/axiosInstance";
import Swal from "sweetalert2";

const LOGIN_PATH = "/login";
const SIGNUP_PATH = "/signup-customer";

export default function PublicShopSurgical() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const requestIdRef = useRef(0);

  const loadProducts = useCallback(async (searchTerm) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setLoadError("");

    try {
      const res = await axiosInstance.get(
        `/surgical/list/?search=${encodeURIComponent(searchTerm.trim())}`
      );

      if (requestId !== requestIdRef.current) return;

      // Support either a plain array or a paginated response.
      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.results)
          ? res.data.results
          : [];

      setProducts(data);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error("Public surgical list failed:", error);
      setLoadError("Surgical items could not be loaded. Please try again.");
      setProducts([]);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadProducts(search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search, loadProducts]);

  const promptLogin = async () => {
    const result = await Swal.fire({
      title: "Login to continue",
      text: "Please log in or create an account to add surgical items to your cart.",
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Login",
      cancelButtonText: "Sign Up",
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#16a34a",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      navigate(LOGIN_PATH, { state: { from: "/shop-surgical" } });
    } else if (result.dismiss === Swal.DismissReason.cancel) {
      navigate(SIGNUP_PATH, { state: { from: "/shop-surgical" } });
    }
  };

  const handleAddToCart = (product) => {
    if (!product || Number(product.stock || 0) <= 0) return;
    // Guest page does not call cart endpoints.
    promptLogin();
  };

  if (loading && products.length === 0 && !loadError) {
    return <LoadingAnimation />;
  }

  return (
    <main className="shop-container">
      <section style={styles.searchWrapper}>
        <div style={styles.introLine}>
          <span>✓ Trusted quality</span>
          <span>✓ Surgical and healthcare essentials</span>
        </div>

        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search surgical items..."
          aria-label="Search surgical items"
          style={styles.searchInput}
        />
      </section>

      {loadError && (
        <div role="alert" style={styles.errorBox}>
          <p>{loadError}</p>
          <button type="button" style={styles.retryButton} onClick={() => loadProducts(search)}>
            Try again
          </button>
        </div>
      )}

      {!loadError && !loading && products.length === 0 && (
        <p style={styles.emptyState}>No surgical items found. Try another search.</p>
      )}

      <section className="product-grid" aria-label="Surgical items">
        {products.map((product) => {
          const price = Number(product.selling_price || product.price || 0);
          const mrp = Number(product.mrp || product.price || 0);
          const stock = Number(product.stock || 0);
          const discount = mrp > price && mrp > 0
            ? Math.round(((mrp - price) / mrp) * 100)
            : 0;
          const images = Array.isArray(product.images) && product.images.length
            ? product.images.filter(Boolean)
            : product.image
              ? [product.image]
              : [];

          return (
            <article key={product.id} style={styles.productCard}>
              <div style={styles.productInfo}>
                <h2 style={styles.productName}>{product.name}</h2>
                {product.salt_composition && (
                  <div style={styles.saltBadge}>🧪 {product.salt_composition}</div>
                )}
                {product.company && (
                  <div style={styles.company}>
                    by <strong>{product.company}</strong>
                  </div>
                )}
              </div>

              <div style={styles.imageFrame}>
                {images.length > 0 ? (
                  <PhotoProvider maskClosable>
                    <Swiper
                      spaceBetween={0}
                      slidesPerView={1}
                      pagination={{ clickable: true }}
                      modules={[Pagination]}
                      style={{ width: "100%", height: "100%" }}
                    >
                      {images.map((imageUrl, index) => (
                        <SwiperSlide key={`${product.id}-${index}`}>
                          <div style={styles.imageSlide}>
                            <PhotoView src={imageUrl}>
                              <img
                                src={imageUrl}
                                alt={product.name || "Surgical item"}
                                loading="lazy"
                                onError={(event) => {
                                  if (!event.currentTarget.dataset.fallback) {
                                    event.currentTarget.dataset.fallback = "true";
                                    event.currentTarget.src = "/no-image.png";
                                  }
                                }}
                                style={styles.productImage}
                              />
                            </PhotoView>
                          </div>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </PhotoProvider>
                ) : (
                  <div style={styles.noImage}>No image available</div>
                )}
              </div>

              <p style={styles.zoomHint}>Tap image to zoom</p>

              <div style={styles.priceBlock}>
                {mrp > price && <del style={styles.mrp}>₹{mrp.toFixed(2)}</del>}
                <div style={styles.price}>₹{price.toFixed(2)}</div>
                {discount > 0 && <div style={styles.discount}>{discount}% OFF</div>}
                {stock <= 0 && <div style={styles.outOfStock}>Out of Stock</div>}
              </div>

              <button
                type="button"
                onClick={() => handleAddToCart(product)}
                disabled={stock <= 0}
                style={{
                  ...styles.addButton,
                  ...(stock <= 0 ? styles.disabledButton : {}),
                }}
              >
                {stock <= 0 ? "Out of Stock" : "Add to Cart"}
              </button>
            </article>
          );
        })}
      </section>

      {loading && products.length > 0 && (
        <div style={styles.loadingMore}><LoadingAnimation small /></div>
      )}
    </main>
  );
}

const styles = {
  searchWrapper: {
    maxWidth: 560,
    margin: "22px auto 26px",
    padding: "0 12px",
    position: "relative",
    boxSizing: "border-box",
  },
  introLine: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: "8px 18px",
    marginBottom: 16,
    color: "#16803d",
    fontSize: 13,
    fontWeight: 650,
    textAlign: "center",
  },
  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "13px 15px",
    borderRadius: 11,
    border: "1px solid #d1d5db",
    background: "#fff",
    fontSize: 14,
    outlineColor: "#2563eb",
  },
  errorBox: {
    maxWidth: 560,
    margin: "20px auto",
    padding: 16,
    borderRadius: 12,
    background: "#fff1f2",
    color: "#9f1239",
    textAlign: "center",
  },
  retryButton: {
    padding: "9px 15px",
    border: 0,
    borderRadius: 8,
    background: "#2563eb",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },
  emptyState: {
    textAlign: "center",
    padding: 24,
    color: "#64748b",
  },
  productCard: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minWidth: 0,
    minHeight: 450,
    padding: 13,
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    background: "#fff",
    boxSizing: "border-box",
    boxShadow: "0 3px 10px rgba(15,23,42,0.04)",
  },
  productInfo: {
    textAlign: "center",
    marginBottom: 8,
  },
  productName: {
    margin: "0 0 7px",
    color: "#1f2937",
    fontSize: 14,
    lineHeight: 1.4,
    fontWeight: 750,
    minHeight: 39,
  },
  saltBadge: {
    display: "inline-block",
    padding: "4px 7px",
    marginBottom: 5,
    borderRadius: 6,
    background: "#eef2ff",
    color: "#3730a3",
    fontSize: 11,
    lineHeight: 1.45,
  },
  company: {
    color: "#64748b",
    fontSize: 11,
    marginTop: 2,
  },
  imageFrame: {
    width: "100%",
    height: 175,
    overflow: "hidden",
    position: "relative",
    textAlign: "center",
  },
  imageSlide: {
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  productImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    cursor: "zoom-in",
  },
  noImage: {
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "#f8fafc",
    color: "#94a3b8",
    fontSize: 12,
  },
  zoomHint: {
    margin: "5px 0 9px",
    color: "#94a3b8",
    textAlign: "center",
    fontSize: 10,
  },
  priceBlock: {
    textAlign: "center",
    marginTop: "auto",
    paddingTop: 7,
    minHeight: 65,
  },
  mrp: {
    display: "block",
    color: "#dc2626",
    fontSize: 12,
  },
  price: {
    color: "#15803d",
    fontSize: 17,
    fontWeight: 700,
  },
  discount: {
    color: "#ea580c",
    fontSize: 12,
    fontWeight: 600,
  },
  outOfStock: {
    color: "#dc2626",
    fontSize: 12,
    fontWeight: 700,
  },
  addButton: {
    width: "100%",
    marginTop: 10,
    padding: "10px 8px",
    border: 0,
    borderRadius: 7,
    background: "#15803d",
    color: "#fff",
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
  },
  disabledButton: {
    background: "#e5e7eb",
    color: "#64748b",
    cursor: "not-allowed",
  },
  loadingMore: {
    display: "flex",
    justifyContent: "center",
    padding: 18,
  },
};
